import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { fetchAcademicYears, AcademicYear } from '../_services/academic.service';

interface AcademicYearContextType {
  selectedYearIds: number[]; // Changed to array for multi-select
  setSelectedYearIds: (yearIds: number[]) => void;
  isAllYearsSelected: boolean;
  toggleYear: (yearId: number) => void;
  toggleAllYears: () => void;
  academicYears: AcademicYear[];
  loading: boolean;
  selectedYears: AcademicYear[];
  ensureYearsLoaded: () => void;
}

const AcademicYearContext = createContext<AcademicYearContextType | undefined>(undefined);

export const useAcademicYear = () => {
  const context = useContext(AcademicYearContext);
  if (!context) {
    throw new Error('useAcademicYear must be used within an AcademicYearProvider');
  }
  // The provider mounts at app root, which may be before login — that first
  // fetch 401s and years stay empty. Retrying from each consumer means the
  // first page that needs years after login triggers the load.
  const { ensureYearsLoaded } = context;
  useEffect(() => {
    ensureYearsLoaded();
  }, [ensureYearsLoaded]);
  return context;
};

interface AcademicYearProviderProps {
  children: ReactNode;
}

export const AcademicYearProvider: React.FC<AcademicYearProviderProps> = ({ children }) => {
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  const inFlightRef = useRef(false);

  const ensureYearsLoaded = useCallback(async () => {
    if (loadedRef.current || inFlightRef.current) return;

    // No point calling the API without a token — it will 401
    const currentUser = localStorage.getItem('currentUser');
    if (!currentUser || currentUser === 'undefined') {
      setLoading(false);
      return;
    }

    inFlightRef.current = true;
    try {
      setLoading(true);
      const response = await fetchAcademicYears();
      setAcademicYears(response.data);
      loadedRef.current = true;

      // Select all years by default (show "All")
      if (response.data.length > 0) {
        setSelectedYearIds(prev => (prev.length === 0 ? response.data.map(year => year.yearId) : prev));
      }
    } catch (error) {
      console.error('Error loading academic years:', error);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    ensureYearsLoaded();
  }, [ensureYearsLoaded]);

  const isAllYearsSelected = selectedYearIds.length === academicYears.length && academicYears.length > 0;

  const toggleYear = (yearId: number) => {
    setSelectedYearIds(prev => {
      if (prev.includes(yearId)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== yearId);
      } else {
        return [...prev, yearId];
      }
    });
  };

  const toggleAllYears = () => {
    if (isAllYearsSelected) {
      // Select only the most recent year instead of deselecting all
      setSelectedYearIds([academicYears[0].yearId]);
    } else {
      // Select all years
      setSelectedYearIds(academicYears.map(year => year.yearId));
    }
  };

  const selectedYears = academicYears.filter(year => selectedYearIds.includes(year.yearId));

  return (
    <AcademicYearContext.Provider
      value={{
        selectedYearIds,
        setSelectedYearIds,
        isAllYearsSelected,
        toggleYear,
        toggleAllYears,
        academicYears,
        loading,
        selectedYears,
        ensureYearsLoaded,
      }}
    >
      {children}
    </AcademicYearContext.Provider>
  );
};
